import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faRotateRight, faHeartPulse } from '@fortawesome/free-solid-svg-icons';
import SettingsModal from './SettingsModal';
import Modal from '../components/Modal';
import { useCPRState } from './CprState/CPRStateContext';
import { useResusContext } from '../Resus/ResusContext';
import CprManager, { CprManagerRef } from './CprManager/CprManager';
import { formatAge as formatAgeUtil } from './CprManager/utils'; // Import the utility function
import {CprBoardReminder } from './Reminders';
import ReminderBox from './ReminderBox';
import './ControlPanel.scss';
import { useNotification } from './Notifications';
import { getHospitalData } from './PerHospitalResusConfig';
import { useTranslation } from 'react-i18next';

interface ControlPanelProps {
  onResetAll: () => void;
  onOpenPatientInfo: () => void;
  isNursingView: boolean;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  onResetAll,
  onOpenPatientInfo,
  isNursingView,
}) => {
  const { showNotification } = useNotification();
  const { state } = useCPRState();
  const { age, weight, protocols, hospital } = useResusContext();
  const { t } = useTranslation(['resus']);
  
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showStartCprModal, setShowStartCprModal] = useState(false);
  const [showCprManager, setShowCprManager] = useState(false);
  const [showBoardReminder, setShowBoardReminder] = useState(false);
  const [initiationState, setInitiationState] = useState(true);
  const cprManagerRef = useRef<CprManagerRef>(null);
  const prevProtocolsRef = useRef(protocols);
  
  const protocolsData = getHospitalData(hospital).protocols;

  useEffect(() => {
    setShowCprManager(state.isRunning || state.status === 'DEATH' || state.status === 'ROSC');
    if (!state.isRunning) {
      setShowBoardReminder(false);
    }
  }, [state.isRunning, state.status]);

    useEffect(() => {
      // don't do anything if we the user didn't put the intial values.
      if (!weight) {
        return;
      }      
      // There are two cases where we want to raise the alert:
      // the regular case - when there was a change in the protocol, and cardiac-arrest was added.
      // the inital case - when the protocols were selected, but the app didn't "start" yet
      // i.e. the initial details were not set.      
      const cardiacArrestAdded = protocols.includes('cardiac-arrest') && !prevProtocolsRef.current.includes('cardiac-arrest');
      const cardiacArrestAddedInInitTime = initiationState && protocols.includes('cardiac-arrest');
      if (!state.isRunning && !state.endTime && (cardiacArrestAdded || cardiacArrestAddedInInitTime)) {
        setTimeout(() => {
          if (state.isRunning || state.endTime) {
            return;
          }
          showNotification({
            icon: faHeartPulse,
            text: t('modals.startCpr.notification'),
            buttons: [
              {
                text: t('modals.startCpr.startButton'),
                onClick: () => {
                  handleStartCprConfirm();
                },
                className: "red-button"
              },
              {
                text: t('modals.startCpr.cancelButton'),
                className: "btn-reset",
                onClick: () => { }
              }
            ]
          });
  
        }, 3000);
      }
      prevProtocolsRef.current = protocols;
      setInitiationState(false);
    }, [protocols, weight, state.isRunning, t])

  const handleResetConfirm = () => {
    onResetAll();
    setShowBoardReminder(false);
    setShowResetModal(false);
    setInitiationState(true);
  };

  const handleStartCprConfirm = () => {
    setShowStartCprModal(false);
    setShowCprManager(true);
    
    setTimeout(() => {
      setShowBoardReminder(true);
    }, 10000);

    setTimeout(() => {
      if (cprManagerRef.current?.handleStartCpr) {
        cprManagerRef.current.handleStartCpr();
      }
    }, 0);
  };

  // Removed local formatAge function

  const formatProtocolNames = (protocolIds: string[]): string => {
    if (!protocolIds.length) return t('protocols.unknownReason');
    
    const protocolNames = protocolIds.map(id => {
      const protocolData = protocolsData.emergencyProtocols
        .flatMap(section => section.protocols)
        .find(p => p.id === id);
      return protocolData?.name || '';
    }).filter(name => name !== '');

    return protocolNames.length ? protocolNames.join(', ') : t('protocols.notSelected');
  };

  return (
    <div className="control-panel-container">
      {!state.isRunning && (
        <div className="control-panel-buttons-container">
          <div className="control-panel-right-buttons">
              {!isNursingView && (
                <button
                className="control-button start-cpr-button red-button"
                  data-testid="control-panel-start-cpr"
                  onClick={() => setShowStartCprModal(true)}
                  disabled={!weight}
                >
                  <FontAwesomeIcon icon={faHeartPulse} />
                  {t('buttons.startCpr')}
                </button>
              )}

            <button 
              className="control-button restart-button" 
              onClick={() => setShowResetModal(true)}
              disabled={!weight}
            >
              <FontAwesomeIcon icon={faRotateRight} />
              {t('buttons.restart')}
            </button>
          </div>
          {!isNursingView && (
          <button
            className="control-button settings-button"
            data-testid="control-panel-settings-button"
            onClick={() => setShowSettingsModal(true)}
          >
            <FontAwesomeIcon icon={faGear} />
            <span className="settings-button-text">{t('buttons.settings')}</span>
          </button>
          )}
        </div>
      )}

      {weight && (
        <div className="control-panel">
          <div className="control-panel-box patient-info"
               onClick={onOpenPatientInfo}>
            <b>{t('protocols.title')}:</b> {formatProtocolNames(protocols)},&nbsp;
            <b>{t('patientInfo.age')}:</b> {formatAgeUtil(age, t) || t('patientInfo.unknown')},&nbsp;
            <b>{t('weight.label')}:</b> {weight ? `${weight} ${t('weight.unit')}` : t('patientInfo.unknown')}
          </div>

          {showCprManager && (
            <div className="control-panel-box cpr-manager">        
              <div className="cpr-manager-wrapper">
                <CprManager ref={cprManagerRef} />
              </div>        
            </div>
          )}
        </div>
      )}
      
      {showBoardReminder && (
        <ReminderBox 
          content={<CprBoardReminder />}
          vibrateDelay={2000}
          onClose={() => setShowBoardReminder(false)}
        />
      )}

      <SettingsModal 
        runningCprMode={state.isRunning}
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <Modal
        isOpen={showResetModal}
        setIsOpen={() => setShowResetModal(false)}
        title={t('modals.restart.title')}
        primaryButton={{
          text: t('buttons.restart'),
          onClick: handleResetConfirm,
        }}
        secondaryButton={{
          text: t('buttons.cancel'),
          onClick: () => setShowResetModal(false),
        }}
      >
        <div>
          {t('modals.restart.confirmMessage')}
        </div>
      </Modal>

      <Modal
        isOpen={showStartCprModal}
        setIsOpen={() => setShowStartCprModal(false)}
        title={t('modals.startCpr.title')}
        primaryButton={{
          text: t('buttons.startCpr'),
          onClick: handleStartCprConfirm,
        }}
        secondaryButton={{
          text: t('buttons.cancel'),
          onClick: () => setShowStartCprModal(false),
        }}
      >
        <div>
          {t('modals.startCpr.confirmMessage')}
        </div>
      </Modal>
    </div>
  );
};

export default ControlPanel;
